import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import LoadingSpinner from '@/components/LoadingSpinner';
import type { Trade } from '@/types';

export default function TradesPage() {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 20;

  useEffect(() => {
    loadTrades();
  }, []);

  const loadTrades = async (loadMore = false) => {
    setIsLoading(true);
    try {
      const newOffset = loadMore ? offset + limit : 0;
      const { trades: newTrades } = await api.getTrades(limit, newOffset);

      if (loadMore) {
        setTrades([...trades, ...newTrades]);
      } else {
        setTrades(newTrades);
      }

      setOffset(newOffset);
      setHasMore(newTrades.length === limit);
    } catch (error) {
      console.error('Failed to load trades:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(value);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-white">Trade History</h1>
        <p className="text-dark-400 text-sm sm:text-base">View all your automated trades</p>
      </div>

      {/* Trades - Desktop Table */}
      <div className="card overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-dark-400 text-sm border-b border-dark-700">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Pair</th>
                <th className="px-4 py-3 font-medium">Chain</th>
                <th className="px-4 py-3 font-medium">Action</th>
                <th className="px-4 py-3 font-medium text-right">Price</th>
                <th className="px-4 py-3 font-medium text-right">Sent</th>
                <th className="px-4 py-3 font-medium text-right">Received</th>
                <th className="px-4 py-3 font-medium">TX</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade) => (
                <tr
                  key={trade.tradeId}
                  className="border-b border-dark-700/50 last:border-0 hover:bg-dark-700/30"
                >
                  <td className="px-4 py-3 text-dark-300 text-sm">
                    {new Date(trade.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-white font-medium">
                      {trade.baseSymbol}/{trade.quoteSymbol}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-dark-300">{trade.chainName}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        trade.action === 'BUY'
                          ? 'bg-green-900/50 text-green-400'
                          : 'bg-red-900/50 text-red-400'
                      }`}
                    >
                      {trade.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-white font-mono">
                    {formatCurrency(trade.price)}
                  </td>
                  <td className="px-4 py-3 text-right text-dark-300 font-mono">
                    {trade.quantitySent.toLocaleString(undefined, {
                      maximumFractionDigits: 6,
                    })}
                  </td>
                  <td className="px-4 py-3 text-right text-dark-300 font-mono">
                    {trade.quantityReceived.toLocaleString(undefined, {
                      maximumFractionDigits: 6,
                    })}
                  </td>
                  <td className="px-4 py-3">
                    {trade.txHash && trade.explorerUrl ? (
                      <a
                        href={`${trade.explorerUrl}/tx/${trade.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-400 hover:text-primary-300 text-sm"
                      >
                        View
                      </a>
                    ) : (
                      <span className="text-dark-500">-</span>
                    )}
                  </td>
                </tr>
              ))}

              {trades.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-dark-400">
                    No trades found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Loading / Load More - Desktop */}
        <div className="p-4 border-t border-dark-700">
          {isLoading ? (
            <div className="flex justify-center">
              <LoadingSpinner />
            </div>
          ) : hasMore && trades.length > 0 ? (
            <button
              onClick={() => loadTrades(true)}
              className="w-full btn btn-secondary"
            >
              Load More
            </button>
          ) : trades.length > 0 ? (
            <p className="text-center text-dark-400 text-sm">
              Showing all {trades.length} trades
            </p>
          ) : null}
        </div>
      </div>

      {/* Trades - Mobile Cards */}
      <div className="md:hidden space-y-3">
        {trades.map((trade) => (
          <div
            key={trade.tradeId}
            className="card p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">
                  {trade.baseSymbol}/{trade.quoteSymbol}
                </span>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                    trade.action === 'BUY'
                      ? 'bg-green-900/50 text-green-400'
                      : 'bg-red-900/50 text-red-400'
                  }`}
                >
                  {trade.action}
                </span>
              </div>
              {trade.txHash && trade.explorerUrl && (
                <a
                  href={`${trade.explorerUrl}/tx/${trade.txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-400 hover:text-primary-300 text-xs"
                >
                  View TX
                </a>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-dark-400 text-xs">Price</p>
                <p className="text-white font-mono">{formatCurrency(trade.price)}</p>
              </div>
              <div>
                <p className="text-dark-400 text-xs">Chain</p>
                <p className="text-dark-200">{trade.chainName}</p>
              </div>
              <div>
                <p className="text-dark-400 text-xs">Sent</p>
                <p className="text-dark-200 font-mono text-xs">
                  {trade.quantitySent.toLocaleString(undefined, { maximumFractionDigits: 6 })}
                </p>
              </div>
              <div>
                <p className="text-dark-400 text-xs">Received</p>
                <p className="text-dark-200 font-mono text-xs">
                  {trade.quantityReceived.toLocaleString(undefined, { maximumFractionDigits: 6 })}
                </p>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-dark-700">
              <p className="text-dark-400 text-xs">
                {new Date(trade.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
        ))}

        {trades.length === 0 && !isLoading && (
          <div className="card text-center py-12">
            <p className="text-dark-400">No trades found</p>
          </div>
        )}

        {/* Loading / Load More - Mobile */}
        {isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : hasMore && trades.length > 0 ? (
          <button
            onClick={() => loadTrades(true)}
            className="w-full btn btn-secondary"
          >
            Load More
          </button>
        ) : trades.length > 0 ? (
          <p className="text-center text-dark-400 text-sm py-2">
            Showing all {trades.length} trades
          </p>
        ) : null}
      </div>
    </div>
  );
}
